import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  reload,
  setPersistence,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { auth } from "../../firebase";
import { getSafeAccessError, platformAccessApi } from "../api/platformAccessApi";
import {
  routeForAccessContext,
  useAccess,
} from "../auth/AccessProvider";
import AccessAlert from "../components/AccessAlert";
import AccessShell from "../components/AccessShell";
import RegistrationStepper from "../components/RegistrationStepper";
import TenantUserRegistrationAccountForm from
  "../components/TenantUserRegistrationAccountForm";
import VerificationPanel from "../components/VerificationPanel";
import {
  getTenantInvitationStage,
  isInvitationEmailMismatchError,
} from "../utils/tenantInvitation";
import { sendBrandedVerificationEmail } from "../utils/sendVerificationEmail";

const VERIFICATION_POLL_MS = 5000;

function getActionCodeSettings(token) {
  return {
    url: `${window.location.origin}/tenant-user/register?invite=${encodeURIComponent(token)}`,
    handleCodeInApp: false,
  };
}

function invitationShape(data) {
  const invitation = data?.invitation || data || {};
  return {
    ...invitation,
    companyDisplayName: invitation.companyDisplayName ||
      invitation.companyName || invitation.displayName,
    maskedEmail: invitation.maskedEmail || invitation.emailMasked,
  };
}

function invitationEmailMismatchMessage(invitation) {
  const recipient = invitation?.maskedEmail || "the invited email address";
  return `This invitation was sent to ${recipient}. Switch accounts to continue securely.`;
}

function stageForMatchingAccount(currentUser, invitation) {
  return getTenantInvitationStage(currentUser, invitation);
}

export default function TenantUserRegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(
    () => String(searchParams.get("invite") || "").trim(),
    [searchParams],
  );
  const {
    loading: accessLoading,
    refreshAccessContext,
    signOutUser,
    user,
  } = useAccess();
  const [stage, setStage] = useState("validating_invitation");
  const [invitation, setInvitation] = useState(null);
  const [displayName, setDisplayName] = useState(
    auth.currentUser?.displayName || "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showExistingLogin, setShowExistingLogin] = useState(false);
  const switchedFromEmail = useRef("");
  const verificationCheckInFlightForUser = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (accessLoading) return () => {
      active = false;
    };
    if (!token) {
      setStage("invalid_or_expired_invitation");
      setError(
        "This registration link is incomplete. Ask your administrator for a new invitation.",
      );
      return () => {
        active = false;
      };
    }

    setStage("validating_invitation");
    setError("");
    const currentUser = user || auth.currentUser;
    platformAccessApi
      .validateTenantUserInvitation(token, currentUser?.email)
      .then(async (result) => {
        if (!active) return;
        const nextInvitation = invitationShape(result);
        if (!nextInvitation.email) {
          throw new Error("The invitation does not contain a registration email.");
        }
        setInvitation(nextInvitation);
        if (!currentUser) {
          setShowExistingLogin(nextInvitation.existingAccountRequired === true);
          setStage("account_form");
          return;
        }
        setDisplayName(currentUser.displayName || nextInvitation.displayName || "");
        const nextStage = stageForMatchingAccount(
          currentUser,
          nextInvitation,
        );
        if (nextStage !== "account_mismatch") {
          setStage(nextStage);
          return;
        }

        const previousEmail = currentUser.email || "the previous account";
        switchedFromEmail.current = previousEmail;
        setMessage(`Switching from ${previousEmail} to the invited account.`);
        try {
          await signOutUser();
          if (!active) return;
          setDisplayName(nextInvitation.displayName || "");
          setShowExistingLogin(
            nextInvitation.existingAccountRequired === true,
          );
          setStage("account_form");
          setError("");
          setMessage(
            `We signed out ${previousEmail}. Continue with the fixed invited address below.`,
          );
        } catch (signOutError) {
          if (!active) return;
          setMessage("");
          setStage("account_mismatch");
          setError(getSafeAccessError(
            signOutError,
            "We could not switch accounts automatically. Try again below.",
          ));
        }
      })
      .catch((nextError) => {
        if (!active) return;
        setStage("invalid_or_expired_invitation");
        setError(getSafeAccessError(
          nextError,
          "This invitation is invalid, expired, or has already been used.",
        ));
      });
    return () => {
      active = false;
    };
  }, [accessLoading, signOutUser, token, user]);

  const createAccount = async ({
    firstName,
    surname,
    email,
    password,
  }) => {
    setBusy(true);
    setError("");
    try {
      const validation = invitationShape(
        await platformAccessApi.validateTenantUserInvitation(token, email),
      );
      setInvitation(validation);
      if (validation.candidateEmailMatchesInvitation !== true ||
          validation.email !== email) {
        setError(invitationEmailMismatchMessage(validation));
        return;
      }
      const name = `${firstName} ${surname}`.trim();
      await setPersistence(auth, browserSessionPersistence);
      const credential = await createUserWithEmailAndPassword(
        auth,
        validation.email,
        password,
      );
      await updateProfile(credential.user, { displayName: name });
      await sendBrandedVerificationEmail(credential.user, {
        continuePath: `/tenant-user/register?invite=${encodeURIComponent(token)}`,
        fallbackSettings: getActionCodeSettings(token),
      });
      setDisplayName(name);
      setStage("email_verification_required");
      setMessage("Your account was created. Verify your email to continue.");
    } catch (nextError) {
      setError(getSafeAccessError(
        nextError,
        "We could not create your account. Please try again.",
      ));
      if (nextError?.code === "auth/email-already-in-use") {
        setShowExistingLogin(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const signInExistingAccount = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await setPersistence(auth, browserSessionPersistence);
      const credential = await signInWithEmailAndPassword(
        auth,
        invitation.email,
        String(data.get("password") || ""),
      );
      await credential.user.getIdToken(true);
      const validation = invitationShape(
        await platformAccessApi.validateTenantUserInvitation(
          token,
          credential.user.email,
        ),
      );
      setInvitation(validation);
      if (validation.candidateEmailMatchesInvitation !== true) {
        setShowExistingLogin(false);
        setStage("account_mismatch");
        setError(invitationEmailMismatchMessage(validation));
        return;
      }
      setDisplayName(credential.user.displayName || validation.displayName || "");
      setStage(stageForMatchingAccount(
        credential.user,
        validation,
      ));
      setShowExistingLogin(false);
    } catch (nextError) {
      setError(getSafeAccessError(
        nextError,
        "We could not sign in to the existing account.",
      ));
    } finally {
      setBusy(false);
    }
  };

  const checkVerification = useCallback(async ({ automatic = false } = {}) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      if (!automatic) navigate("/login", { replace: true });
      return false;
    }
    const uid = currentUser.uid;
    if (verificationCheckInFlightForUser.current === uid) return false;
    verificationCheckInFlightForUser.current = uid;
    if (!automatic) {
      setBusy(true);
      setError("");
    }
    try {
      await reload(currentUser);
      if (!mounted.current || auth.currentUser?.uid !== uid) return false;
      if (!currentUser.emailVerified) {
        if (!automatic) {
          setError(
            "Your email is not verified yet. Complete verification, then try again.",
          );
        }
        return false;
      }
      await currentUser.getIdToken(true);
      const validation = invitationShape(
        await platformAccessApi.validateTenantUserInvitation(
          token,
          currentUser.email,
        ),
      );
      setInvitation(validation);
      if (validation.candidateEmailMatchesInvitation !== true) {
        setStage("account_mismatch");
        setError(invitationEmailMismatchMessage(validation));
        return false;
      }
      setError("");
      setMessage("Email verified. Your invitation is ready to accept.");
      setStage("accepting_invitation");
      return true;
    } catch (nextError) {
      if (!automatic && mounted.current) {
        setError(getSafeAccessError(
          nextError,
          "We could not confirm email verification.",
        ));
      }
      return false;
    } finally {
      if (verificationCheckInFlightForUser.current === uid) {
        verificationCheckInFlightForUser.current = null;
      }
      if (!automatic && mounted.current) setBusy(false);
    }
  }, [navigate, token]);

  useEffect(() => {
    if (stage !== "email_verification_required" || busy) return undefined;
    const runCheck = () => {
      if (document.visibilityState === "visible") {
        void checkVerification({ automatic: true });
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") runCheck();
    };
    runCheck();
    const interval = window.setInterval(runCheck, VERIFICATION_POLL_MS);
    window.addEventListener("focus", runCheck);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", runCheck);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [busy, checkVerification, stage]);

  const switchToInvitedAccount = async (showLogin) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await signOutUser();
      setDisplayName(invitation?.displayName || "");
      setShowExistingLogin(showLogin);
      setStage("account_form");
    } catch (nextError) {
      setError(getSafeAccessError(
        nextError,
        "We could not switch accounts. Please try again.",
      ));
    } finally {
      setBusy(false);
    }
  };

  const resendVerification = async () => {
    if (!auth.currentUser) return;
    setBusy(true);
    setError("");
    try {
      await sendBrandedVerificationEmail(auth.currentUser, {
        continuePath: `/tenant-user/register?invite=${encodeURIComponent(token)}`,
        fallbackSettings: getActionCodeSettings(token),
      });
      setMessage("A new verification email has been sent.");
    } catch (nextError) {
      setError(getSafeAccessError(
        nextError,
        "We could not resend verification. Please wait and try again.",
      ));
    } finally {
      setBusy(false);
    }
  };

  const completeInvitation = async () => {
    setBusy(true);
    setError("");
    try {
      const currentUser = auth.currentUser;
      if (!currentUser?.emailVerified) {
        throw new Error("Verify your email before accepting the invitation.");
      }
      await currentUser.getIdToken(true);
      await platformAccessApi.completeTenantUserRegistration(
        token,
        displayName || currentUser.displayName || currentUser.email,
      );
      const context = await refreshAccessContext({ forceTokenRefresh: true });
      if (!context?.activeTenant) {
        throw new Error("Tenant access could not be confirmed.");
      }
      navigate(routeForAccessContext(context), { replace: true });
    } catch (nextError) {
      if (isInvitationEmailMismatchError(nextError)) {
        setStage("account_mismatch");
        setError(invitationEmailMismatchMessage(invitation));
      } else {
        setError(getSafeAccessError(
          nextError,
          "We could not accept this invitation. Confirm that you are using the invited account.",
        ));
      }
    } finally {
      setBusy(false);
    }
  };

  const activeStep = [
    "account_form",
    "account_mismatch",
  ].includes(stage) ? 0 :
    stage === "email_verification_required" ? 1 : 2;
  const downloadsOnly = invitation?.productRole === "download_recipient";

  return (
    <AccessShell
      eyebrow="Secure invitation"
      title={stage === "invalid_or_expired_invitation" ?
        "Invitation unavailable." : invitation?.existingAccountRequired === true ?
          "Sign in with your existing account." : "Create your account."}
      description={invitation?.companyDisplayName ?
        downloadsOnly ? `Access programme downloads shared by ${invitation.companyDisplayName}.` : `Join ${invitation.companyDisplayName} on AndUs.` :
        "Use your secure tenant invitation to get started."}
      footer={(
        <p>
          {invitation?.existingAccountRequired === true ?
            downloadsOnly ? "Use your existing AndUs account to accept this invitation." : "Use the same credentials you use for the legacy learning dashboard." : (
              <>
                Already have an account?{" "}
                <button
                  className="pa-inline-link"
                  type="button"
                  onClick={() => setShowExistingLogin(true)}
                >
                  Sign in
                </button>
              </>
            )}
        </p>
      )}
    >
      {!['invalid_or_expired_invitation', 'validating_invitation'].includes(stage) ?
        <RegistrationStepper activeStep={activeStep} /> : null}
      <AccessAlert tone="error">{error}</AccessAlert>
      <AccessAlert tone="success">{message}</AccessAlert>

      {stage === "validating_invitation" ? (
        <div className="pa-inline-loader"><span />Validating your invitation…</div>
      ) : null}
      {stage === "invalid_or_expired_invitation" ? (
        <div className="pa-empty-action">
          <p>Tenant registration is available only through a valid invitation.</p>
          <Link className="pa-primary-button" to="/login">
            <span>Return to sign in</span><span>›</span>
          </Link>
        </div>
      ) : null}
      {stage === "account_mismatch" && !showExistingLogin ? (
        <div className="pa-accept-panel">
          <div className="pa-invitation-workspace">
            <span>Different account required</span>
            <strong>{invitation?.companyDisplayName}</strong>
            <small>Invitation for {invitation?.maskedEmail}</small>
          </div>
          <p>
            Only the verified account matching this invitation can accept it.
          </p>
          <button
            className="pa-primary-button"
            type="button"
            onClick={() => switchToInvitedAccount(
              invitation?.existingAccountRequired === true,
            )}
            disabled={busy}
          >
            <span>{busy ? "Switching accounts…" :
              invitation?.existingAccountRequired === true ?
                "Sign in with the invited account" :
                "Create the invited account"}</span>
            <span aria-hidden="true">›</span>
          </button>
          {invitation?.existingAccountRequired !== true ? (
            <button
              className="pa-text-button"
              type="button"
              onClick={() => switchToInvitedAccount(true)}
              disabled={busy}
            >
              Sign in with the invited account
            </button>
          ) : null}
        </div>
      ) : null}
      {stage === "account_form" && !showExistingLogin && invitation &&
        invitation.existingAccountRequired !== true ? (
        <TenantUserRegistrationAccountForm
          invitation={invitation}
          onSubmit={createAccount}
          submitting={busy}
        />
      ) : null}
      {showExistingLogin && invitation ? (
        <form className="pa-form pa-existing-login" onSubmit={signInExistingAccount}>
          <div className="pa-invitation-workspace">
            <span>Existing account</span>
            <strong>Sign in to accept this invitation</strong>
          </div>
          <label className="pa-field">
            <span>Work email</span>
            <input
              name="email"
              type="email"
              value={invitation.email}
              readOnly
              aria-readonly="true"
              required
            />
          </label>
          <label className="pa-field">
            <span>Password</span>
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              required
            />
          </label>
          <button className="pa-primary-button" type="submit" disabled={busy}>
            <span>{busy ? "Signing in…" : "Sign in and continue"}</span>
            <span>›</span>
          </button>
          {invitation.existingAccountRequired !== true ? (
            <button
              className="pa-text-button"
              type="button"
              onClick={() => setShowExistingLogin(false)}
            >
              Create a new account instead
            </button>
          ) : null}
        </form>
      ) : null}
      {stage === "email_verification_required" && !showExistingLogin ? (
        <VerificationPanel
          email={auth.currentUser?.email}
          onContinue={() => checkVerification()}
          onResend={resendVerification}
          busy={busy}
        />
      ) : null}
      {stage === "accepting_invitation" && !showExistingLogin ? (
        <div className="pa-accept-panel">
          <div className="pa-invitation-workspace">
            <span>Verified invitation</span>
            <strong>{invitation?.companyDisplayName}</strong>
            <small>
              {[invitation?.productLabel, invitation?.roleLabel]
                .filter(Boolean)
                .join(" · ")}
            </small>
            {downloadsOnly && invitation?.programName ? <strong>{invitation.programName}</strong> : null}
          </div>
          <p>
            {downloadsOnly ? "Accept to open your programme download dashboard, with PDF guides and a SCORM package for the programme shared with you." : "Accepting creates an ordinary tenant membership with only the product access assigned in this invitation."}
          </p>
          <button
            className="pa-primary-button"
            type="button"
            onClick={completeInvitation}
            disabled={busy}
          >
            <span>{busy ? "Activating access…" : "Accept invitation"}</span>
            <span>›</span>
          </button>
        </div>
      ) : null}
    </AccessShell>
  );
}

export {
  getActionCodeSettings,
  invitationShape,
  stageForMatchingAccount,
};
